import { useState } from 'react';
import type { Deal, SCData, TabState, Touchpoint } from '../../types';
import { buildSCList, buildTouchpointSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { parseWfDate } from '../../utils/date';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
type DealViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-259" data-mp="3m00mhcyklgdm">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-260" data-mp="3h1yug2j626q0" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-261" data-mp="3vrehp8jhnu0y" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-262" data-mp="3vt7rgwpeeud6" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-263" data-mp="3qt37uxt71jiq" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-264" data-mp="3338umf4ypsxy">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-265" data-mp="2y4o3d57oujln" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-266" data-mp="3c2vzjawwhgvr" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-267" data-mp="3c4p6krbfe34a" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-268" data-mp="381k5mt6zu9hr" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-269" data-mp="rcwsr4rldduq">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-270" data-mp="nc358ci2a12d" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-271" data-mp="ndebciyzbkwn" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-272" data-mp="3g4qmrvs4dnoy" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-273" data-mp="3l4wf7e67re83" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-274" data-mp="3l3blfqrq3zba" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-275" data-mp="3q495k8wan3ff" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-276" data-mp="3q4r5bfi5zzs3" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-277" data-mp="3u5uoobzf8dy2" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-278" data-mp="3u38h4ccj5yfu" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-279" data-mp="32eesbq82bcun">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-280" data-mp="36fqipxyrg23w" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-281" data-mp="36dw0irevio3i" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-282" data-mp="3b7qpa99c5t9g" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-283" data-mp="2eo19mqbdaqcq">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-284" data-mp="2iobbprw1gwmb" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-285" data-mp="2b7es2xzj8ckv" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-286" data-mp="2b63r15xh1ixg" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-287" data-mp="268205ytt0fbf" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-288" data-mp="268bn1djjy6cw" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-289" data-mp="224vsqnuygi3d" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-290" data-mp="1x62h70z8b15u" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-291" data-mp="1s8am5y3aeyxf" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: DealViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  d,
  maxLen = 35
}: {
  d: Deal;
  maxLen?: number;
}) {
  return <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={d.requestName} data-id="element-292" data-mp="39llp5n9tf946">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-293" data-mp="2ztpqob3thgzf">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-294" data-mp="2pwl1e2eod5ee" />
        <path d="M7.5 1.5h3v3" data-id="element-295" data-mp="2bx26ibavjqk1" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-296" data-mp="2by4x0i4abqzf" />
      </svg>
      {d.requestName.substring(0, maxLen)}
    </a>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: DealViewMode;
  onChange: (m: DealViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-297" data-mp="f1nunn56jouq">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-298" data-mp="1zmauzqolzl8f">
          <opt.icon data-id="element-299" data-mp="29cnnq66kukpn" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 30) return '0–30 days';
  if (age <= 60) return '31–60 days';
  if (age <= 90) return '61–90 days';
  return '90+ days';
}
const AGE_BUCKETS = ['0–30 days', '31–60 days', '61–90 days', '90+ days'];
function groupByAgeBucket(deals: Deal[]): Record<string, Deal[]> {
  const groups: Record<string, Deal[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const d of deals) {
    const b = ageBucket(d.age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(d);
  }
  return groups;
}

/* ─── Display-only age for in-progress deals ──────────────────────────── */

function displayAge(d: Deal): number | null {
  if (d.age !== null) return d.age;
  if (d.status !== 'In Progress' || !d.entry) return null;
  const entryDate = parseWfDate(d.entry);
  if (!entryDate) return null;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((Date.now() - entryDate.getTime()) / MS_PER_DAY);
}

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  deals
}: {
  deals: Deal[];
}) {
  return <table className="deal-table" data-id="element-300" data-mp="25keawwmro7dn">
      <thead data-id="element-301" data-mp="25jl4jwrpzjw1">
        <tr data-id="element-302" data-mp="2acxq1y13rakk">
          <th style={{
          width: '9%'
        }} data-id="element-303" data-mp="2ace5sanzca3d">ID</th>
          <th style={{
          width: '16%'
        }} data-id="element-304" data-mp="2adfrra6jagzv">Account</th>
          <th style={{
          width: '18%'
        }} data-id="element-305" data-mp="2fcadokzsslo7">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-306" data-mp="2fdv5i2uq9qid">Age</th>
          <th style={{
          width: '8%'
        }} data-id="element-307" data-mp="2kbdf7vgkk9rf">Date</th>
          <th style={{
          width: '9%'
        }} data-id="element-308" data-mp="2kcoilu63rfi0">GNARR</th>
          <th style={{
          width: '9%'
        }} data-id="element-309" data-mp="2p9f10o5dqn6l">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-310" data-mp="2pa6ikv256kdv">Offering</th>
          <th style={{
          width: '10%'
        }} data-id="element-311" data-mp="dn4i3tdglhnf">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-312" data-mp="8ni4vjxms1em">
        {deals.map(d => <tr key={d.id} data-id="element-313" data-mp="8mp4d0p9m84x">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-314" data-mp="8pl1wnx7ausu">{d.id}</td>
            <td title={d.account} data-id="element-315" data-mp="3p6rx0c1has6">{d.account.substring(0, 24)}</td>
            <td title={d.requestName} data-id="element-316" data-mp="3uuyiu1xn4rg1">
              <RequestLink d={d} maxLen={30} data-id="element-317" data-mp="3uw00ip95wc6q" />
            </td>
            <td className={ageSeverity(displayAge(d)) === 'high' ? 'c-red' : ageSeverity(displayAge(d)) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(displayAge(d)) === 'high' ? 600 : 400
        }} data-id="element-318" data-mp="wdm5difivl1e">
              {formatAge(displayAge(d))}
            </td>
            <td style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-319" data-mp="mkwvut3j8h8p">{d.entry}</td>
            <td data-id="element-320" data-mp="hnn8dizpefni">{fmtG(d.gnarr)}</td>
            <td data-id="element-321" data-mp="1f3kbhszrf42e">{d.close_q}</td>
            <td data-id="element-322" data-mp="1f3u1vhopvd8m"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-323" data-mp="1f3u6juznw1ey">{d.offering.substring(0, 18)}</span></td>
            <td data-id="element-324" data-mp="1absti6uo66nv"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-325" data-mp="1ac2a53ttr1gw">{d.sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-tile-grid" data-id="element-326" data-mp="nhkbcc2bs0b9">
      {deals.map(d => <div key={d.id} className="deal-tile" title={`${d.account}\n${d.requestName}\nAge: ${formatAge(displayAge(d))}\nGNARR: ${fmtG(d.gnarr)}\nDate: ${d.entry}`} data-id="element-327" data-mp="9p4ddpoa6sh7">
          <div className="dt-id" data-id="element-328" data-mp="elb9kqqvkply">{d.id}</div>
          <div className="dt-acct" data-id="element-329" data-mp="12ebcoqvk7m3i">{d.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-330" data-mp="16elhi07m86gs">
            <RequestLink d={d} maxLen={16} data-id="element-331" data-mp="16cr6bdm5bspw" />
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-332" data-mp="se0zh69iyeoc">{d.entry}</div>
          <span className={`dt-age ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-333" data-mp="xddwgju4124n">
            {formatAge(displayAge(d))}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-gallery" data-id="element-334" data-mp="3aju1j2iean7r">
      {deals.map(d => <div key={d.id} className="deal-gallery-card" data-id="element-335" data-mp="35lshae98ghup">
          <div className="dg-id" data-id="element-336" data-mp="35laawosuwn92">{d.id}</div>
          <div className="dg-title" data-id="element-337" data-mp="3jlbcy7kuy0q4">{d.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-338" data-mp="3jl1myjztbtkf">
            <RequestLink d={d} maxLen={40} data-id="element-339" data-mp="3elystuldf7r8" />
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-340" data-mp="3ekdv5w3rf8p5">{d.entry}</div>
          <div className="dg-meta" data-id="element-341" data-mp="2qx5q9fa8u1nn">
            <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-342" data-mp="2mvki278nmbjs">{formatAge(displayAge(d))}</span>
            <span className="badge b-blue" data-id="element-343" data-mp="2vvz6vgktu8s1">{fmtG(d.gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-344" data-mp="2vufs5uetk5sd">{d.close_q}</span>
            {isAged(d) && <span className="badge b-red" data-id="element-345" data-mp="284tnwsjlb0di">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-346" data-mp="236a4aysgy0ob">
            {d.sub_region} · {d.offering.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  deals
}: {
  deals: Deal[];
}) {
  const groups = groupByAgeBucket(deals);
  return <div className="deal-waterfall" data-id="element-347" data-mp="3vtnqm01cw8cc">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-348" data-mp="3rr0uhmxnk19c">
            <div className="deal-waterfall-lane-header" data-id="element-349" data-mp="3mszb10wbdzxc">
              <span data-id="element-350" data-mp="3muk7wx63puyd">{bucket}</span>
              <span className="lane-count" data-id="element-351" data-mp="3mushog6xgk5c">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-352" data-mp="3i067ggcl0xmu">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-353" data-mp="3d3fvo57t8o78">none</div>}
              {items.map(d => <div key={d.id} className="deal-waterfall-card" data-id="element-354" data-mp="341yh4ra1wukn">
                  <div className="dw-id" data-id="element-355" data-mp="2z3504ovk2lfq">{d.id}</div>
                  <div className="dw-acct" title={d.account} data-id="element-356" data-mp="2z5hr1rjak64x">{d.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-357" data-mp="1engpay4rzexb">
                    <RequestLink d={d} maxLen={20} data-id="element-358" data-mp="1jivzng3kqsoz" />
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-359" data-mp="1nlskzeki12wm">{d.entry}</div>
                  <div className="dw-tags" data-id="element-360" data-mp="1sldpe4ifu3gq">
                    <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-361" data-mp="1sj18luof1z59">{formatAge(displayAge(d))}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-362" data-mp="14vjc5lu7icvy">{fmtG(d.gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  if (deals.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-363" data-mp="1kgck7eqquh18">No in-progress deals.</div>;
  }
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...deals].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  switch (mode) {
    case 'table':
      return <TableView deals={sorted} data-id="element-364" data-mp="11j1lpwcxrrmu" />;
    case 'tile':
      return <TileView deals={sorted} data-id="element-365" data-mp="wix6sarof4yq" />;
    case 'gallery':
      return <GalleryView deals={sorted} data-id="element-366" data-mp="wki0xza2wlw2" />;
    case 'waterfall':
      return <WaterfallView deals={sorted} data-id="element-367" data-mp="1aj7xndszkjjw" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-368" data-mp="15jvb6u611ehu">
          {sorted.map(d => <div key={d.id} className="deal-chip" data-id="element-369" data-mp="15lpwi9l7n8mg">
              <div className="dc-id" data-id="element-370" data-mp="2lx28ngcy50sr">{d.id}</div>
              <div className="dc-name" data-id="element-371" data-mp="2lzy673kvtngo">{d.account.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-372" data-mp="2gxz5y2kfv6ny"><RequestLink d={d} data-id="element-373" data-mp="2pfsnv8w37hcg" /></div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-374" data-mp="2pepxd22ofgx2">{d.entry}</div>
              <div className="dc-tags" data-id="element-375" data-mp="2thmlt9ewe78x">
            <span className={`badge ${ageSeverity(displayAge(d)) === 'high' ? 'b-red' : ageSeverity(displayAge(d)) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-376" data-mp="2tipcbg8b67ob">{formatAge(displayAge(d))}</span>
                <span className="badge b-blue" data-id="element-377" data-mp="2kh7xs2ajue1q">{fmtG(d.gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-378" data-mp="3860o9j8ejtoy">{d.close_q}</span>
                {isAged(d) && <span className="badge b-red" data-id="element-379" data-mp="3d4bwjpj1tw52">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-380" data-mp="2ygz91v9olgc1">
                {d.sub_region} · {d.offering.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-381" data-mp="3v82rdhvh1tja">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-382" data-mp="3v8chr6kfi2pi">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-383" data-mp="8tjfbrtyyqfz">
        <tr data-id="element-384" data-mp="8sqgdd19558e">
          <th data-id="element-385" data-mp="8ubch737sw8d">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-386" data-mp="drunly4m8sw3">{c}</th>)}
          <th data-id="element-387" data-mp="dq05erkqbevp">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-388" data-mp="iqefef5w4ywd">
        {scRows.map(row => <tr key={row.full} data-id="element-389" data-mp="irpei1oe5ih5">
            <td title={row.full} data-id="element-390" data-mp="iobev1743nde">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-391" data-mp="ms9d79om4u5e">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-392" data-mp="2jtoxb7njavmw">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-393" data-mp="2etsu3amnv88e">—</span>}
              </td>)}
            <td data-id="element-394" data-mp="2skxnvw3cl6at"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-395" data-mp="2skxpg0izxecx">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-396" data-mp="2ntfo3bd29brf">
        <tr data-id="element-397" data-mp="2nsxtsk9yl7m5">
          <td data-id="element-398" data-mp="2ns4l3drfw81d">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-399" data-mp="32i27pp9q0pog"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-400" data-mp="32hk5mc0dnh8m">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-401" data-mp="2xjir84dwc63h"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-402" data-mp="2xjsfnnjam572">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Touchpoint expanded view ──────────────────────────────────────────── */

function TouchpointExpandedView({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: DealViewMode;
}) {
  if (touchpoints.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '12px 4px',
      textAlign: 'center'
    }} data-id="element-403" data-mp="1d4p647sztfr1">No touchpoints for this SC</div>;
  }
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...touchpoints].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  switch (mode) {
    case 'table':
      return <TouchpointTableView touchpoints={sorted} data-id="element-404" data-mp="y7yyrtyr2qiu" />;
    case 'tile':
      return <TouchpointTileView touchpoints={sorted} data-id="element-405" data-mp="jb7ylih95as6" />;
    case 'gallery':
      return <TouchpointGalleryView touchpoints={sorted} data-id="element-406" data-mp="ob41tfi4ky6o" />;
    case 'waterfall':
      return <TouchpointWaterfallView touchpoints={sorted} data-id="element-407" data-mp="o8zsuj5dd8zj" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-408" data-mp="adxldrkvjbkm">
          {sorted.map(t => <div key={t.id} className="deal-chip" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-409" data-mp="fb7jrwq8qxkl">
              <div style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-410" data-mp="29op4lbz9hgbx">{t.id}</div>
              <div style={{
            fontSize: 10,
            fontWeight: 600,
            marginTop: 2
          }} data-id="element-411" data-mp="29mmd0nignf53">{t.account.substring(0, 16)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 2
          }} data-id="element-412" data-mp="3iayy3uocxwr6">
                <span title={t.name} data-id="element-413" data-mp="3dbckvl8j4gid">{t.name.substring(0, 20)}{t.name.length > 20 ? '…' : ''}</span>
              </div>
              <div style={{
            display: 'flex',
            gap: 4,
            marginTop: 4,
            flexWrap: 'wrap'
          }} data-id="element-414" data-mp="38hi4aqo80hni">
                {t.touchpointType && <span className="badge b-blue" style={{
              fontSize: 8
            }} data-id="element-415" data-mp="34fn4xrpuoecd">{t.touchpointType}</span>}
                <span style={{
              fontSize: 8,
              color: '#667085'
            }} data-id="element-416" data-mp="2zgjwryekps06">{t.entry}</span>
              </div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-417" data-mp="2ulmr91gi60s1">{t.sub_region} · {t.close_q}</div>
            </div>)}
        </div>;
  }
}

/* ─── Touchpoint view renderers ──────────────────────────────────────────── */

function TouchpointTableView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const tpColumns: SortableColumn<Touchpoint>[] = [{
    key: 'id',
    label: 'ID',
    width: '10%',
    sortValue: t => t.id,
    render: t => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-418" data-mp="21pwydou1ncrw">{t.id}</span>
  }, {
    key: 'name',
    label: 'Name',
    width: '16%',
    sortValue: t => t.name,
    render: t => <span title={t.name} data-id="element-419" data-mp="26lvlza7jsqr9">{t.name.substring(0, 22)}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '14%',
    sortValue: t => t.account,
    render: t => <span title={t.account} data-id="element-420" data-mp="2ff8lww414smu">{t.account.substring(0, 18)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '10%',
    sortValue: t => t.sc,
    render: t => <span data-id="element-421" data-mp="1npcib0fijv23">{t.sc}</span>
  }, {
    key: 'touchpointType',
    label: 'Type',
    width: '12%',
    sortValue: t => t.touchpointType,
    render: t => t.touchpointType ? <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-422" data-mp="1rq66luwvbbvi">{t.touchpointType}</span> : <span data-id="element-423" data-mp="1wqambipyc2ko">—</span>
  }, {
    key: 'entry',
    label: 'Entry',
    width: '10%',
    sortValue: t => t.entry,
    render: t => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-424" data-mp="3352zxh034n8o">{t.entry}</span>
  }, {
    key: 'solutionComments',
    label: 'Comments',
    width: '18%',
    sortValue: t => t.solutionComments,
    render: t => <span title={t.solutionComments} data-id="element-425" data-mp="39movctlmj9y0">{t.solutionComments ? t.solutionComments.substring(0, 30) + (t.solutionComments.length > 30 ? '…' : '') : '—'}</span>
  }];
  return <SortableTable columns={tpColumns} data={touchpoints} rowKey={t => t.id} data-id="element-426" data-mp="3jbqbcjs65rbz" />;
}
function TouchpointTileView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-tile-grid" data-id="element-427" data-mp="3oa1j8oywlrrk">
      {touchpoints.map(t => <div key={t.id} className="deal-tile" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-428" data-mp="2qo48xeuogsqp">
          <div className="dt-id" data-id="element-429" data-mp="2vo8fod5sa93q">{t.id}</div>
          <div className="dt-acct" data-id="element-430" data-mp="30kpcdvr1tvsf">{t.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-431" data-mp="30nl9xiyziigc">
            <span title={t.name} data-id="element-432" data-mp="35lmuy9fz0rug">{t.name.substring(0, 16)}</span>
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-433" data-mp="fu83icceckbk">{t.entry}</div>
          <span className="badge b-blue" style={{
        fontSize: 8
      }} data-id="element-434" data-mp="kt1jqcj2ipff">{t.touchpointType || '—'}</span>
        </div>)}
    </div>;
}
function TouchpointGalleryView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-gallery" data-id="element-435" data-mp="20dk893cjaf98">
      {touchpoints.map(t => <div key={t.id} className="deal-gallery-card" data-id="element-436" data-mp="1vlk4eztcf27d">
          <div className="dg-id" data-id="element-437" data-mp="1vjzbfemofrbm">{t.id}</div>
          <div className="dg-title" data-id="element-438" data-mp="1qm7j4ji4e36w">{t.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-439" data-mp="1qleje2hkwdy9">
            <span title={t.name} data-id="element-440" data-mp="1loxjkb10ob5c">{t.name.substring(0, 30)}{t.name.length > 30 ? '…' : ''}</span>
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-441" data-mp="1hkznk96znggi">{t.entry}</div>
          <div style={{
        fontSize: 9,
        color: '#344054',
        marginBottom: 4,
        lineHeight: 1.3
      }} data-id="element-442" data-mp="1co973kr9uiuk">
            <span title={t.solutionComments} data-id="element-443" data-mp="17pohef9nbhkd">{t.solutionComments ? t.solutionComments.substring(0, 50) + (t.solutionComments.length > 50 ? '…' : '') : '—'}</span>
          </div>
          <div className="dg-meta" data-id="element-444" data-mp="321w3d33zoo40">
            {t.touchpointType && <span className="badge b-blue" data-id="element-445" data-mp="320b9lfpi1977">{t.touchpointType}</span>}
            <span className="badge b-gray" data-id="element-446" data-mp="10cglaxvtdv2a">{t.close_q}</span>
          </div>
          <div className="dg-footer" data-id="element-447" data-mp="1546wt76hmova">
            {t.sub_region} · {t.sc}
          </div>
        </div>)}
    </div>;
}
const TP_TYPE_BUCKETS = ['Internal Alignment', 'Customer Facing', 'No Type'];
function TouchpointWaterfallView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const groups: Record<string, Touchpoint[]> = {};
  for (const b of TP_TYPE_BUCKETS) groups[b] = [];
  for (const t of touchpoints) {
    const bucket = t.touchpointType || 'No Type';
    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(t);
  }
  return <div className="deal-waterfall" data-id="element-448" data-mp="1e4mziefeprz3">
      {TP_TYPE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-449" data-mp="3tupzkdkmvpgv">
            <div className="deal-waterfall-lane-header" data-id="element-450" data-mp="2pgi5aapgjiv">
              <span data-id="element-451" data-mp="2r1act9jrqdk">{bucket}</span>
              <span className="lane-count" data-id="element-452" data-mp="2gx8zbsc48u1o">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-453" data-mp="2gzc097et47l6">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-454" data-mp="2c08tnij6htb3">none</div>}
              {items.map(t => <div key={t.id} className="deal-waterfall-card" data-id="element-455" data-mp="2lwtyo3v774ex">
                  <div className="dw-id" data-id="element-456" data-mp="1y5yd3ohc55g4">{t.id}</div>
                  <div className="dw-acct" title={t.account} data-id="element-457" data-mp="1y4n51cguxbj7">{t.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-458" data-mp="1t5k340w6blfg">
                    <span title={t.name} data-id="element-459" data-mp="270me32fvo0z4">{t.name.substring(0, 20)}</span>
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-460" data-mp="226s08fmyeg7y">{t.entry}</div>
                  <div className="dw-tags" data-id="element-461" data-mp="1ehfr1frmm85n">
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-462" data-mp="1ah5cvftokbfp">{t.touchpointType || '—'}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<DealViewMode>('cards');
  const [expandedViewMode, setExpandedViewMode] = useState<'deals' | 'touchpoints'>('deals');
  const [scSearch, setScSearch] = useState('');
  const {
    deals,
    touchpoints
  } = useDeals();

  // Touchpoints filters
  const [tpSearchName, setTpSearchName] = useState('');
  const [tpSearchSc, setTpSearchSc] = useState('');
  const [tpTypeFilter, setTpTypeFilter] = useState<string[]>([]);
  const [tpTypeDropdownOpen, setTpTypeDropdownOpen] = useState(false);
  const tpTypes = [...new Set(touchpoints.map(t => t.touchpointType).filter(Boolean))].sort();
  const touchpointsFiltered = touchpoints.filter(t => {
    if (tpSearchName && !t.name.toLowerCase().includes(tpSearchName.toLowerCase())) return false;
    if (tpSearchSc && !t.sc.toLowerCase().includes(tpSearchSc.toLowerCase())) return false;
    if (tpTypeFilter.length > 0 && !tpTypeFilter.includes(t.touchpointType)) return false;
    return true;
  });
  const scDataAll = buildSCList(state.r, state.sub, state, deals);
  const tpSCDataAll = buildTouchpointSCList(state.r, state.sub, state, touchpoints);
  const tpSCMap = new Map(tpSCDataAll.map(tp => [tp.n, tp]));
  const scFiltered = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allForGnarr);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '22%',
    render: p => <span className="name-link" data-id="element-463" data-mp="1c0gpzt53q5mp">
          {p.n}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-464" data-mp="x24u3opjz0wl">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-465" data-mp="ieo06fdfww1i">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-466" data-mp="ihimydjxesy3" />
          </div>
          <strong data-id="element-467" data-mp="eg74qt3dt9zx">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-468" data-mp="nd7q0qv5mrz5">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-469" data-mp="11diddjx99ofk" />
          </div>
          <strong data-id="element-470" data-mp="xcgcyzrlr6c0">{p.closed}</strong>
        </>
  }, {
    key: 'avgAge',
    label: 'Avg age (Closed)',
    width: '11%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-471" data-mp="166biwa0jt0sp">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age (Closed)',
    width: '11%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }, {
    key: 'gnarr',
    label: 'GNARR (IP)',
    width: '14%',
    sortValue: p => p.gnarr,
    render: p => <>{fmtG(p.gnarr)}</>
  }, {
    key: 'closedGnarr',
    label: 'GNARR (Closed)',
    width: '14%',
    sortValue: p => p.closedGnarr,
    render: p => <>{fmtG(p.closedGnarr)}</>
  }, {
    key: 'tpCount',
    label: 'TP',
    width: '6%',
    sortValue: p => tpSCMap.get(p.n)?.count ?? 0,
    render: p => {
      const count = tpSCMap.get(p.n)?.count ?? 0;
      return <span style={{
        color: count > 0 ? '#3B82F6' : '#8892a0',
        fontWeight: count > 0 ? 600 : 400
      }} data-id="element-472" data-mp="3j54metloson7">{count}</span>;
    }
  }];
  return <div data-id="element-473" data-mp="3sv6d7t5fcl5t">
      {/* Metrics */}
      <div className="mrow" data-id="element-474" data-mp="3svg37gqgysbi">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-475" data-mp="3swr7rirqo43o" />
        <MetricCard label="Requests / person" value={scFiltered.length ? +(allIP.length / scFiltered.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-476" data-mp="30edhq5cn9mf5" />
        <MedianAgeCard ages={ages} data-id="element-477" data-mp="1hh4yg07gj1q5" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-478" data-mp="1dhxa04v3l1ej" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-479" data-mp="18hrnt47lk73u" />
      <div className="tw" data-id="element-480" data-mp="13jgcep1nloji">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-481" data-mp="13k99eyat8zog">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-482" data-mp="yl6gudc0olxd" />
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={p => {
        if (expandedSC === p.n) {
          setExpandedSC(null);
        } else {
          setExpandedSC(p.n);
          setExpandedViewMode('deals');
        }
      }} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-484" data-mp="3kx8chvu54r1w">
              <td colSpan={8} style={{
          padding: 0
        }} data-id="element-485" data-mp="3ky9lzvvid5hi">
                <div className="exp-inner" data-id="element-486" data-mp="2x7499pkv6tbf">
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }} data-id="element-487" data-mp="2x85kbu1vrft5">
                    <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#667085',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                flex: 1
              }} data-id="element-488" data-mp="2t854c4z4uwjz">
                      {expandedViewMode === 'deals' ? <>In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)</> : <>Touchpoints — {p.n} ({tpSCMap.get(p.n)?.count ?? 0} touchpoints)</>}
                    </div>
                    <div style={{
                display: 'flex',
                border: '1px solid #d0d5dd',
                borderRadius: 5,
                overflow: 'hidden'
              }} data-id="element-489" data-mp="p0isr3svtlq7">
                      <button onClick={() => setExpandedViewMode('deals')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  background: expandedViewMode === 'deals' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'deals' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'deals' ? 600 : 400
                }} data-id="element-490" data-mp="ts0x81u464fx">
                        Deals
                      </button>
                      <button onClick={() => setExpandedViewMode('touchpoints')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  borderLeft: '1px solid #d0d5dd',
                  cursor: 'pointer',
                  background: expandedViewMode === 'touchpoints' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'touchpoints' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'touchpoints' ? 600 : 400
                }} data-id="element-491" data-mp="13odqiylqyga5">
                        Touchpoints
                      </button>
                    </div>
                    <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-492" data-mp="2lmju3yzlcqd0" />
                  </div>
                  {expandedViewMode === 'deals' ? <>
                      <DealExpandedView deals={p.deals} mode={dealView} data-id="element-493" data-mp="2gq1pr086au17" />
                      {p.closedDeals.length > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-494" data-mp="27obzhxq12150">
                            Closed — {p.n} ({p.closed} deals)
                          </div>
                          <DealExpandedView deals={p.closedDeals} mode={dealView} data-id="element-495" data-mp="dfg95tasly8s" />
                        </>}
                    </> : <TouchpointExpandedView touchpoints={tpSCMap.get(p.n)?.touchpoints ?? []} mode={dealView} data-id="element-496" data-mp="ibwxanhzsclv" />}
                </div>
              </td>
            </tr> : null} data-id="element-483" data-mp="fvzxqp7b9sb1" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-497" data-mp="3qn04orf0alnl" />
      <div className="tw" data-id="element-498" data-mp="3vk00m9i5680z">
        <div className="sub" data-id="element-499" data-mp="3vlbg36ky947x">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-500" data-mp="8g3nhbvnqx7s">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-501" data-mp="8h537tnmc7vu">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-502" data-mp="1fuf4zu9qdv27" />
          </table>
        </div>
      </div>

      {/* Touchpoints — deal reviews */}
      <SectionHeader title="Touchpoints — deal reviews" data-id="element-503" data-mp="1ie3ffd32hlej" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }} data-id="element-504" data-mp="1iev381qfaeu9">
        <input type="text" placeholder="Search name…" value={tpSearchName} onChange={e => setTpSearchName(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-505" data-mp="1dga98k1rkre9" />
        <input type="text" placeholder="Search SC…" value={tpSearchSc} onChange={e => setTpSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-506" data-mp="1wemkqcg5edd0" />
        <div style={{
        position: 'relative'
      }} data-id="element-507" data-mp="2g1u12fsupstq">
          <button onClick={() => setTpTypeDropdownOpen(!tpTypeDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-508" data-mp="2g353ab6nesiq">
            {tpTypeFilter.length > 0 ? `Type (${tpTypeFilter.length})` : 'Type ▾'}
          </button>
          {tpTypeDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-509" data-mp="2fyboqkrlpl8e">
              <div onClick={() => setTpTypeFilter(tpTypeFilter.length === tpTypes.length ? [] : [...tpTypes])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-510" data-mp="2b132o93c0x1v">
                {tpTypeFilter.length === tpTypes.length ? 'Deselect all' : 'Select all'}
              </div>
              {tpTypes.map(t => <label key={t} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-511" data-mp="2qu0fac5c7ch">
                  <input type="checkbox" checked={tpTypeFilter.includes(t)} onChange={() => {
              setTpTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
            }} data-id="element-512" data-mp="qhhiupjoufda" />
                  {t}
                </label>)}
            </div>}
        </div>
      </div>
      <div className="tw" data-id="element-513" data-mp="2x8ytqaoxtzlb">
        <SortableTable columns={[{
        key: 'id',
        label: 'ID',
        width: '10%',
        sortValue: (t: Touchpoint) => t.id,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-515" data-mp="2s44qppndjwps">{t.id}</span>
      }, {
        key: 'name',
        label: 'Name',
        width: '18%',
        sortValue: (t: Touchpoint) => t.name,
        render: (t: Touchpoint) => <span title={t.name} data-id="element-516" data-mp="31ty1wpjlu9ti">{t.name.substring(0, 24)}</span>
      }, {
        key: 'account',
        label: 'Account',
        width: '14%',
        sortValue: (t: Touchpoint) => t.account,
        render: (t: Touchpoint) => <span title={t.account} data-id="element-517" data-mp="3aumhvpsk6k91">{t.account.substring(0, 18)}</span>
      }, {
        key: 'sc',
        label: 'SC',
        width: '10%',
        sortValue: (t: Touchpoint) => t.sc,
        render: (t: Touchpoint) => <span title={t.sc} data-id="element-518" data-mp="3frv2ry53d2dz">{t.sc}</span>
      }, {
        key: 'touchpointType',
        label: 'Type',
        width: '12%',
        sortValue: (t: Touchpoint) => t.touchpointType,
        render: (t: Touchpoint) => t.touchpointType ? <span className="badge b-blue" style={{
          fontSize: 8
        }} data-id="element-519" data-mp="1qerlpoqn37ev">{t.touchpointType}</span> : <span style={{
          color: '#d1d5db'
        }} data-id="element-520" data-mp="1qgvno14z9wci">—</span>
      }, {
        key: 'entry',
        label: 'Entry',
        width: '12%',
        sortValue: (t: Touchpoint) => t.entry,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-521" data-mp="3ncyls9jf56rt">{t.entry}</span>
      }, {
        key: 'solutionComments',
        label: 'Comments',
        width: '16%',
        sortValue: (t: Touchpoint) => t.solutionComments,
        render: (t: Touchpoint) => <span title={t.solutionComments} data-id="element-522" data-mp="39dfq4g7snnwe">{t.solutionComments ? t.solutionComments.substring(0, 35) + (t.solutionComments.length > 35 ? '…' : '') : '—'}</span>
      }]} data={touchpointsFiltered} rowKey={(t: Touchpoint) => t.id} defaultSort={{
        key: 'entry',
        dir: 'desc'
      }} data-id="element-514" data-mp="2x8pb58682upn" />
      </div>
    </div>;
}